/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IComponentStyles } from '@fluentui/foundation-legacy';
import { DefaultButton, PrimaryButton } from '@fluentui/react/lib/Button';
import { Stack } from '@fluentui/react/lib/Stack';
import { ITextProps, ITextSlots } from '@fluentui/react/lib/Text';
import { ITextFieldStyleProps, ITextFieldStyles } from '@fluentui/react/lib/TextField';
import { classNamesFunction } from '@fluentui/utilities';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { CopyButton } from 'src/core/components/CopyButton';
import { LuisText } from 'src/core/components/LuisText';
import { LuisTextField } from 'src/core/components/LuisTextField';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { logDemoButtonClick, logNextSteps } from 'src/core/services/telemetry/eventsNamesTemplate';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { TelemetryFeatureTitle } from 'src/core/types/telemetryTypes';
import { commonDemosMessages } from 'src/languageStudio/appShell/prebuiltDemos/utils/commonDemoMessages';

type NextStepsRunCodeScreenProps = {
    sdkLink?: string;
    curlCommand: string;
    samplesLink: string;
    tryoutName: TelemetryFeatureTitle;
    isResultsEmpty: boolean;
};

const messages = defineMessages({
    getSdk: { id: 'languageStudio.nextStepsRunCodeScreen.getSdk', defaultMessage: 'Get SDK' },
    sdkTitle: { id: 'languageStudio.nextStepsRunCodeScreen.sdkTitle', defaultMessage: '1. Get SDK & Code samples' },
    viewSamples: { id: 'languageStudio.nextStepsRunCodeScreen.viewSamples', defaultMessage: 'View samples on GitHub' },
    useCodeTitle: { id: 'languageStudio.nextStepsRunCodeScreen.useCodeTitle', defaultMessage: '2. Use the code in your own environment' },
    useCodeDescription: {
        id: 'languageStudio.nextStepsRunCodeScreen.useCodeDescription',
        defaultMessage: `Run this code in your own development environment using a CURL request or check out the API references for other languages.`
    },
    sdkDescription: {
        id: 'languageStudio.nextStepsRunCodeScreen.sdkDescription',
        defaultMessage: `Use {tryoutName} in your own application.
        Start with sample code and then deploy the feature to your application with the {tryoutName} SDK.`
    }
});

const theme = getScopedTheme('DemoPage');
const textStyles = classNamesFunction<ITextProps, IComponentStyles<ITextSlots>>()({ root: { marginLeft: 20 } });

const curlTextFieldStyles = classNamesFunction<ITextFieldStyleProps, ITextFieldStyles>()({
    root: { marginLeft: '20px' }, // Ensure CSS values are strings or numbers
    fieldGroup: { minHeight: '200px' },
    field: { color: theme?.disabledTextFieldTextColor || '#A6A6A6' } // Provide a fallback
});

export const NextStepsRunCodeScreen = (props: NextStepsRunCodeScreenProps) => {
    const { curlCommand, tryoutName, sdkLink, isResultsEmpty, samplesLink } = props;
    const { localize } = useLocalization();

    const handleClickLink = (linkName: string) => {
        logDemoButtonClick(tryoutName, 'Nextsteps:runCode', linkName);
        if (!isResultsEmpty) {
            logNextSteps();
        }
    };

    return (
        <Stack tokens={{ childrenGap: 25 }}>
            <Stack tokens={{ childrenGap: 8 }}>
                <LuisText variant="mediumPlus" as="h3" fontWeight={600}>
                    {localize(messages.sdkTitle)}
                </LuisText>
                <LuisText styles={textStyles}>{localize(messages.sdkDescription, { tryoutName })}</LuisText>
            </Stack>

            <Stack horizontal tokens={{ childrenGap: 10, padding: '0 0 0 20px' }}>
                {sdkLink && (
                    <PrimaryButton
                        href={sdkLink}
                        target="_blank"
                        iconProps={{ iconName: 'Code' }}
                        text={localize(messages.getSdk)}
                        onClick={() => handleClickLink('SDK')}
                    />
                )}
                <DefaultButton
                    target="_blank"
                    href={samplesLink}
                    iconProps={{ iconName: 'Github' }}
                    text={localize(messages.viewSamples)}
                    onClick={() => handleClickLink('GithubSamples')}
                />
            </Stack>

            <Stack tokens={{ childrenGap: 8 }}>
                <LuisText variant="mediumPlus" as="h3" fontWeight={600}>
                    {localize(messages.useCodeTitle)}
                </LuisText>
                <LuisText styles={textStyles}>{localize(messages.useCodeDescription)}</LuisText>
            </Stack>

            <LuisTextField
                readOnly
                disabled
                multiline
                value={curlCommand}
                styles={curlTextFieldStyles}
                onRenderLabel={() => (
                    <Stack horizontal verticalAlign="end" horizontalAlign="end" tokens={{ padding: '0 0 5px 0' }}>
                        <CopyButton
                            label=""
                            textToCopy={curlCommand}
                            styles={{ root: { height: 'auto' } }}
                            text={localize(commonDemosMessages.copy)}
                        />
                    </Stack>
                )}
            />
        </Stack>
    );
};
